import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Box, IconButton, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import SearchFilters from '../components/dashboard/bucket-documents/SearchFilters';
import BucketDocumentsTable from '../components/dashboard/bucket-documents/BucketDocumentsTable';

const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const BucketDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter and sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/upload/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch documents');
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err.message || 'Error fetching documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Filter documents based on search and category
  const getFilteredDocuments = () => {
    return documents; // Filtering is handled in BucketDocumentsTable
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  };

  const handleDeleteDocument = async (document) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/api/upload/documents/${encodeURIComponent(document.key)}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete document');
      }

      // Remove the document from local state
      setDocuments(prevDocs => prevDocs.filter(doc => doc.key !== document.key));
      setError(''); // Clear any previous errors

      // Show success message temporarily
      const successMessage = 'Document deleted successfully';
      setError('');
      // You could add a success state here if needed

    } catch (err) {
      console.error('Delete document error:', err);
      setError(`Failed to delete document: ${err.message}`);
      throw err; // Re-throw so the component can handle loading states
    }
  };

  const handleDeleteDocuments = async (documentsToDelete) => {
    try {
      const token = localStorage.getItem('token');
      const deletePromises = documentsToDelete.map(document =>
        fetch(`${apiBase}/api/upload/documents/${encodeURIComponent(document.key)}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      );

      const responses = await Promise.allSettled(deletePromises);
      
      // Check for any failures
      const failures = responses.filter(result => result.status === 'rejected' || !result.value.ok);
      
      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} out of ${documentsToDelete.length} documents`);
      }

      // Remove the documents from local state
      const deletedKeys = new Set(documentsToDelete.map(doc => doc.key));
      setDocuments(prevDocs => prevDocs.filter(doc => !deletedKeys.has(doc.key)));
      setError(''); // Clear any previous errors

    } catch (err) {
      console.error('Bulk delete error:', err);
      setError(`Failed to delete documents: ${err.message}`);
      throw err; // Re-throw so the component can handle loading states
    }
  };

  const filteredDocuments = getFilteredDocuments();

  return (
    <AdminPageLayout
      title="Bucket Documents"
      subtitle="Manage and view uploaded documents in the S3 bucket"
      maxWidth="xl"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Tooltip title="Refresh documents">
          <IconButton 
            onClick={fetchDocuments}
            disabled={loading}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search and Filters */}
      <SearchFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onClearFilters={handleClearFilters}
        totalDocuments={documents.length}
        filteredDocuments={filteredDocuments.length}
      />

      {/* Documents Table */}
      <BucketDocumentsTable
        documents={documents}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        searchTerm={searchTerm}
        categoryFilter={categoryFilter}
        onDeleteDocument={handleDeleteDocument}
        onDeleteDocuments={handleDeleteDocuments}
      />
    </AdminPageLayout>
  );
};

export default BucketDocuments; 